import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { map, filter, take, timeout, catchError, of, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PermissionService } from '../services/permission.service';
import { KeycloakService } from '../services/keycloak.service';

/**
 * Module path constants for auto-detection
 */
export const MODULE_PATHS = {
  // Main menu (the disabling menus will be updated later)
  CHANNEL: '/channel',
  POS: '/pos',
  SPU: '/goods',
  IMS: '/stock',
  WMS: '/warehouse',
  PROCUREMENT: '/procurement',
  OMS: '/order',
  O2O: '/o2o',
  MARKETING: '/marketing',

  // Settings (the disabling sub-menus will be updated later)
  ST_PRODUCT: '/configuration/product',
  ST_INVENTORY: '/configuration/inventory',
  ST_PROCUREMENT: '/configuration/procurement',
  ST_ORDER: '/configuration/order',
  ST_FINANCE: '/configuration/finance',
  ST_MASTER_DATA: 'configuration/master-data',
  ST_FILE: 'configuration/file',
} as const;

/**
 * Inbound route constants for tab-based navigation
 */
export const INBOUND_ROUTES = {
  BASE: '/stock/inbound',
  TAB_NOTICE: 'inbound-notice',
  TAB_WARRANT: 'inbound-warrant',
  TAB_RESULT: 'inbound-result',
} as const;

/**
 * Route data interface for advanced permission redirect strategy
 */
export interface PermissionRedirectStrategy {
  /** Base permission module (e.g., P_CHANNEL, P_POS, etc.) */
  modulePermissions: {
    READ: string;
    CREATE: string;
    UPDATE: string;
    DELETE?: string;
    APPROVE?: string;
  };
  /** Module name for auto-detection (e.g., 'CHANNEL', 'POS'). If provided, basePath will be auto-detected */
  moduleName?: keyof typeof MODULE_PATHS;
  /** Base path for redirects (e.g., '/channel/retail-store'). If not provided, auto-detected from URL or moduleName */
  basePath?: string;
  /** Route type: 'list' | 'create' | 'edit' | 'view'. If not provided, auto-detected from URL */
  routeType?: 'list' | 'create' | 'edit' | 'view';
}

/**
 * Permission Guard
 * Protects routes based on user permissions
 *
 * Basic usage:
 * {
 *   path: 'channel',
 *   component: ChannelComponent,
 *   canActivate: [permissionGuard],
 *   data: { permissions: [P_CHANNEL.READ] }
 * }
 *
 * Advanced usage with redirect strategy (for create/edit routes):
 * {
 *   path: 'channel/retail-store/add',
 *   component: RetailStoreAddComponent,
 *   canActivate: [permissionGuard],
 *   data: {
 *     permissions: [P_CHANNEL.CREATE],
 *     redirectStrategy: {
 *       modulePermissions: P_CHANNEL,
 *       basePath: '/channel/retail-store',
 *       routeType: 'create'
 *     }
 *   }
 * }
 */
export const permissionGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
):
  | Observable<boolean | UrlTree>
  | Promise<boolean | UrlTree>
  | boolean
  | UrlTree => {
  const permissionService = inject(PermissionService);
  const keycloakService = inject(KeycloakService);
  const router = inject(Router);

  if (
    !keycloakService.isReady() ||
    !keycloakService.isAuthenticated() ||
    (!keycloakService.isSessionValid() && !keycloakService.hasValidRefreshToken())
  ) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  // Get required permissions from route data
  const requiredPermissions = route.data['permissions'] as string[];
  const routePath = route.data['route'] as string | undefined;
  const redirectStrategy = route.data['redirectStrategy'] as
    | PermissionRedirectStrategy
    | undefined;

  // NEW PERMISSION FLOW: Prioritize route-based checks
  // If route is specified, use new API-driven route permission check
  if (routePath) {
    return permissionService.permissionsLoaded$.pipe(
      filter((loaded) => loaded),
      take(1),
      map(() => {
        const canAccess = permissionService.canAccessRoute(routePath);
        if (!canAccess) {
          console.warn(
            `[PermissionGuard] Route access denied: ${routePath} (URL: ${state.url})`,
          );
          return router.createUrlTree(['/home'], {
            queryParams: {
              returnUrl: state.url,
              reason: 'insufficient_permissions',
            },
          });
        }
        return true;
      }),
      timeout(3000),
      catchError(() => {
        // On timeout, check synchronously
        const canAccess = permissionService.canAccessRoute(routePath);
        if (!canAccess) {
          console.warn(
            `[PermissionGuard] Route access denied (timeout): ${routePath}`,
          );
        }
        return of(
          canAccess ||
            router.createUrlTree(['/home'], {
              queryParams: {
                returnUrl: state.url,
                reason: 'insufficient_permissions',
              },
            }),
        );
      }),
    );
  }

  // LEGACY PERMISSION FLOW: Fall back to old permission-based checks
  // If no route specified but permissions are required, use legacy check
  if (!requiredPermissions || requiredPermissions.length === 0) {
    // No route and no permissions required - allow access
    return true;
  }

  // Helper function to check permissions and apply redirect strategy
  const checkPermissionsWithStrategy = (
    permissions: string[],
  ): boolean | UrlTree => {
    const hasRequiredPermission = permissionService.checkPermission(
      requiredPermissions,
      permissions,
    );

    if (hasRequiredPermission) {
      return true;
    }

    // If redirect strategy is provided, apply smart redirect logic
    if (redirectStrategy) {
      return applyRedirectStrategy(
        permissions,
        redirectStrategy,
        state.url,
        router,
        state,
        route,
        permissionService,
      );
    }

    // Default: redirect to home
    return router.createUrlTree(['/home'], {
      queryParams: {
        returnUrl: state.url,
        reason: 'insufficient_permissions',
      },
    });
  };

  // Wait for permissions to be loaded
  return permissionService.permissionsLoaded$.pipe(
    filter((loaded) => loaded),
    take(1),
    switchMap(() => {
      // First, try synchronous permission check - this is fast and works if permissions are already loaded
      const currentPermissions = permissionService.getCurrentPermissions();
      if (currentPermissions.length > 0) {
        const result = checkPermissionsWithStrategy(currentPermissions);
        if (result === true) {
          // Permissions are loaded and user has access - allow immediately
          // Only refresh token in background if it's expired or about to expire (non-blocking)
          const tokenParsed = keycloakService.getTokenParsed();
          const isExpired = keycloakService.isTokenExpired();
          let needsRefresh = isExpired;

          if (!isExpired && tokenParsed?.exp) {
            const now = Math.floor(Date.now() / 1000);
            const timeUntilExpiry = tokenParsed.exp - now;
            // Only refresh if token has less than 60 seconds left
            needsRefresh = timeUntilExpiry < 60;
          }

          if (needsRefresh && keycloakService.hasValidRefreshToken()) {
            keycloakService.updateToken(60).catch(() => {
              // Silently fail - token refresh is not critical for navigation
            });
          }
          return of(true);
        }
        // Return redirect if needed
        return of(result);
      }

      // If permissions not loaded yet, check if Keycloak is authenticated
      // On page refresh, permissions might not be loaded yet but user is authenticated
      const isAuthenticated = keycloakService.isAuthenticated();
      const isReady = keycloakService.isReady();

      if (!isAuthenticated || !isReady) {
        return of(
          router.createUrlTree(['/login'], {
            queryParams: { returnUrl: state.url },
          }),
        );
      }

      // Try synchronous check first (permissions might already be loaded)
      const syncPermissions = permissionService.getCurrentPermissions();
      if (syncPermissions.length > 0) {
        const result = checkPermissionsWithStrategy(syncPermissions);
        return of(result);
      }

      // If not available synchronously, wait for permissions to reload
      return permissionService.userPermissions$.pipe(
        // Filter to wait for permissions to be loaded (non-empty array)
        filter((userPermissions) => {
          // If we have permissions, proceed
          if (userPermissions.length > 0) {
            return true;
          }
          // If empty, check if permissions are actually loaded via synchronous check
          const syncPermissions = permissionService.getCurrentPermissions();
          return syncPermissions.length > 0;
        }),
        take(1), // Take the first emission that passes the filter
        timeout(3000), // Timeout for page refresh scenarios
        map((userPermissions) => {
          // Use the observable value or fallback to synchronous check
          const permissionsToCheck =
            userPermissions.length > 0
              ? userPermissions
              : permissionService.getCurrentPermissions();

          const result = checkPermissionsWithStrategy(permissionsToCheck);

          if (result === true) {
            return true;
          } else {
            // Log for debugging
            console.warn(
              `[PermissionGuard] Access denied for route: ${
                state.url
              }. Required: ${requiredPermissions.join(
                ', ',
              )}, User has: ${permissionsToCheck.slice(0, 10).join(', ')}${
                permissionsToCheck.length > 10 ? '...' : ''
              }`,
            );

            return result;
          }
        }),
        catchError((error) => {
          // On timeout or error, do a final synchronous check
          console.warn(
            `[PermissionGuard] Timeout or error checking permissions, doing final sync check:`,
            error,
          );
          const finalPermissions = permissionService.getCurrentPermissions();

          // If Keycloak is authenticated but permissions still aren't loaded,
          // this might be a page refresh scenario - allow access and let permissions load
          if (isAuthenticated && isReady && finalPermissions.length === 0) {
            console.warn(
              `[PermissionGuard] Keycloak authenticated but permissions not loaded yet (likely page refresh). Allowing access.`,
            );
            // Permissions will load soon via APP_INITIALIZER
            // Allow access for now - if user doesn't have permission, they'll be blocked on next navigation
            return of(true);
          }

          const result = checkPermissionsWithStrategy(finalPermissions);

          if (result === true) {
            return of(true);
          } else {
            console.warn(
              `[PermissionGuard] Final check failed. Required: ${requiredPermissions.join(
                ', ',
              )}, User has: ${finalPermissions.slice(0, 10).join(', ')}`,
            );
            return of(result);
          }
        }),
      );
    }),
    timeout(5000), // Overall timeout
    catchError((error) => {
      // On error, do a final synchronous check
      console.warn(
        `[PermissionGuard] Error during permission check, doing final sync check:`,
        error,
      );
      const finalPermissions = permissionService.getCurrentPermissions();
      const result = checkPermissionsWithStrategy(finalPermissions);

      if (result === true) {
        return of(true);
      } else {
        return of(
          router.createUrlTree(['/home'], {
            queryParams: {
              returnUrl: state.url,
              reason: 'insufficient_permissions',
            },
          }),
        );
      }
    }),
  );
};

function detectRouteType(
  url: string,
  strategy: PermissionRedirectStrategy,
): 'create' | 'edit' | 'view' | 'list' {
  if (strategy.routeType) return strategy.routeType;
  const urlWithoutQuery = url.split('?')[0];
  if (urlWithoutQuery.includes('/add') || urlWithoutQuery.endsWith('/add'))
    return 'create';
  if (urlWithoutQuery.includes('/edit') || urlWithoutQuery.endsWith('/edit'))
    return 'edit';
  if (urlWithoutQuery.includes('/view') || urlWithoutQuery.endsWith('/view'))
    return 'view';
  return 'list';
}

function detectBasePath(
  url: string,
  strategy: PermissionRedirectStrategy,
): string {
  if (strategy.basePath) return strategy.basePath;
  const urlWithoutQuery = url.split('?')[0];
  const { moduleName } = strategy;
  if (moduleName && MODULE_PATHS[moduleName]) {
    const modulePath = MODULE_PATHS[moduleName];
    const cleanedUrl = urlWithoutQuery
      .replace(modulePath, '')
      .replace(/\/add.*$/, '')
      .replace(/\/edit.*$/, '')
      .replace(/\/view.*$/, '');
    return cleanedUrl ? `${modulePath}${cleanedUrl}` : modulePath;
  }
  const cleanedUrl = urlWithoutQuery
    .replace(/\/add.*$/, '')
    .replace(/\/edit.*$/, '')
    .replace(/\/view.*$/, '');
  const match = /^(\/[^/]+(?:\/[^/]+)*)/.exec(cleanedUrl);
  return match ? match[1] : '/home';
}

function buildInsufficientPermissionsTree(
  router: Router,
  state: RouterStateSnapshot,
): UrlTree {
  return router.createUrlTree(['/home'], {
    queryParams: { returnUrl: state.url, reason: 'insufficient_permissions' },
  });
}

function extractInboundTab(
  route: ActivatedRouteSnapshot | undefined,
  url: string,
  state: RouterStateSnapshot,
): string | null {
  if (route?.queryParams?.['tab']) {
    return route.queryParams['tab'];
  }
  const tabFromUrl = extractTabFromQueryString(url);
  if (tabFromUrl) return tabFromUrl;
  const tabFromState = extractTabFromQueryString(state.url);
  if (tabFromState) return tabFromState;
  return extractTabFromWindow();
}

function extractTabFromQueryString(
  urlString: string | null | undefined,
): string | null {
  if (!urlString?.includes('tab=')) return null;
  const queryString = urlString.split('?')[1] || '';
  return queryString ? new URLSearchParams(queryString).get('tab') : null;
}

function extractTabFromWindow(): string | null {
  if (globalThis.window === undefined) return null;
  if (globalThis.window.location.href.includes('tab=')) {
    try {
      return new URL(globalThis.window.location.href).searchParams.get('tab');
    } catch {
      const qs =
        globalThis.window.location.href.split('?')[1]?.split('#')[0] || '';
      return qs ? new URLSearchParams(qs).get('tab') : null;
    }
  }
  return globalThis.window.location.search
    ? new URLSearchParams(globalThis.window.location.search).get('tab')
    : null;
}

function resolveActionRouteTree(
  hasPermission: boolean,
  canFallbackToList: boolean,
  basePath: string,
  route: ActivatedRouteSnapshot | undefined,
  url: string,
  state: RouterStateSnapshot,
  router: Router,
): UrlTree {
  if (!hasPermission) {
    if (canFallbackToList) {
      const redirect = getRedirectPath(basePath, route, url, state);
      if (redirect.queryParams) {
        return router.createUrlTree([redirect.path], {
          queryParams: redirect.queryParams,
        });
      }
      return router.createUrlTree([redirect.path]);
    }
    return buildInsufficientPermissionsTree(router, state);
  }
  return router.createUrlTree(['/home']);
}

function getRedirectPath(
  path: string,
  route: ActivatedRouteSnapshot | undefined,
  url: string,
  state: RouterStateSnapshot,
): { path: string; queryParams?: Record<string, string> } {
  if (!path.startsWith(INBOUND_ROUTES.BASE)) {
    return { path };
  }
  const tab = extractInboundTab(route, url, state);
  if (
    tab &&
    (tab === INBOUND_ROUTES.TAB_NOTICE ||
      tab === INBOUND_ROUTES.TAB_WARRANT ||
      tab === INBOUND_ROUTES.TAB_RESULT)
  ) {
    return { path: INBOUND_ROUTES.BASE, queryParams: { tab } };
  }
  return { path: INBOUND_ROUTES.BASE };
}

/**
 * Apply redirect strategy based on module permissions and route type
 */
function applyRedirectStrategy(
  permissions: string[],
  strategy: PermissionRedirectStrategy,
  url: string,
  router: Router,
  state: RouterStateSnapshot,
  route: ActivatedRouteSnapshot | undefined,
  permissionService: PermissionService,
): UrlTree {
  const { modulePermissions } = strategy;
  const routeType = detectRouteType(url, strategy);
  const basePath = detectBasePath(url, strategy);

  const hasRead = permissionService.checkPermission(
    [modulePermissions.READ],
    permissions,
  );
  const hasCreate = permissionService.checkPermission(
    [modulePermissions.CREATE],
    permissions,
  );
  const hasUpdate = permissionService.checkPermission(
    [modulePermissions.UPDATE],
    permissions,
  );
  const hasAnyModulePermission =
    hasRead ||
    hasCreate ||
    hasUpdate ||
    (modulePermissions.DELETE &&
      permissionService.checkPermission(
        [modulePermissions.DELETE],
        permissions,
      )) ||
    (modulePermissions.APPROVE &&
      permissionService.checkPermission(
        [modulePermissions.APPROVE],
        permissions,
      ));

  if (routeType === 'list' || routeType === 'view') {
    return hasRead
      ? router.createUrlTree(['/home'])
      : buildInsufficientPermissionsTree(router, state);
  }

  if (routeType === 'create') {
    return resolveActionRouteTree(
      hasCreate,
      !!hasAnyModulePermission && hasRead,
      basePath,
      route,
      url,
      state,
      router,
    );
  }

  if (routeType === 'edit') {
    return resolveActionRouteTree(
      hasUpdate,
      !!hasAnyModulePermission && hasRead,
      basePath,
      route,
      url,
      state,
      router,
    );
  }

  return buildInsufficientPermissionsTree(router, state);
}
